import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Alert, Badge, Spinner, Modal, Form, Table } from "react-bootstrap";
import {
  CheckCircle,
  ExclamationTriangleFill,
  InfoCircleFill,
  Calendar,
  Globe,
  ArrowRepeat,
  EyeFill,
  PlayFill
} from 'react-bootstrap-icons';
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import "../components/SchoolAdmin.css";
import {
  whoami,
  getCountries,
  getNextYearClassConfig,
  updateNextYearClassConfig,
  getAcademicYearConfigs,
  createAcademicYearConfig,
  updateAcademicYearConfig,
  deleteAcademicYearConfig,
  bulkTransitionGradeClasses
} from "../services/APIClient";
import { isTeacher } from "../utils/auth";

function AcademicYearControl() {
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [name, setName] = useState("User");
  const [teacherId, setTeacherId] = useState(null);

  const teacherMode = isTeacher();

  // Loading and messaging states
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showStartMonthModal, setShowStartMonthModal] = useState(false);

  // Config data
  const [countries, setCountries] = useState([]);
  const [countryConfigs, setCountryConfigs] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingCountries, setSavingCountries] = useState(new Set());

  // Start month config data
  // shape: { [countryId]: { startMonth: number, exists: bool } }
  const [startMonthConfigs, setStartMonthConfigs] = useState({});
  const [loadingStartMonth, setLoadingStartMonth] = useState(false);
  const [savingStartMonth, setSavingStartMonth] = useState(new Set());
  const [startMonthError, setStartMonthError] = useState(null);
  const [startMonthSuccess, setStartMonthSuccess] = useState(null);

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Available academic years (hardcoded for now, matching your mockup)
  const AVAILABLE_YEARS = ["25-26", "26-27", "27-28", "28-29", "30-31"];

  // Grade levels this endpoint can transition from (transitions to gradeLevel + 1)
  const TRANSITION_GRADE_LEVELS = [1, 2, 3];

  // ─── Bulk Grade Transition state ───────────────────────────────────────────
  const [showGradeTransitionModal, setShowGradeTransitionModal] = useState(false);
  const [transitionGradeLevel, setTransitionGradeLevel] = useState(1);
  const [transitionSourceYear, setTransitionSourceYear] = useState("");
  const [transitionNewYear, setTransitionNewYear] = useState("");
  const [transitionReason, setTransitionReason] = useState("");
  const [transitionAttempt, setTransitionAttempt] = useState(1);
  const [transitionPreviewResult, setTransitionPreviewResult] = useState(null);
  const [transitionLiveResult, setTransitionLiveResult] = useState(null);
  const [transitionPreviewing, setTransitionPreviewing] = useState(false);
  const [transitionRunning, setTransitionRunning] = useState(false);
  const [transitionConfirmChecked, setTransitionConfirmChecked] = useState(false);
  const [transitionError, setTransitionError] = useState(null);

  const transitionSelectionValid =
    transitionSourceYear && transitionNewYear && transitionSourceYear !== transitionNewYear;

  // Deterministic per-selection key so an accidental duplicate click (or a network
  // retry) replays the same cached result instead of re-executing. Preview and live
  // runs get distinct keys since they carry different request bodies (dryRun differs),
  // and "Run again" only bumps the live key when a genuinely new attempt is wanted.
  const buildTransitionIdempotencyKey = ({ dryRun, attempt }) => {
    const base = `grade-transition-g${transitionGradeLevel}-${transitionSourceYear}-${transitionNewYear}-${dryRun ? "preview" : "live"}`;
    return attempt > 1 ? `${base}-run${attempt}` : base;
  };

  const getTransitionSummaryText = () => {
    if (!transitionSourceYear || !transitionNewYear) {
      return "Choose a grade level and both academic years to see what this will do.";
    }
    if (transitionSourceYear === transitionNewYear) {
      return "Source and target academic years must be different.";
    }

    const nextGrade = transitionGradeLevel + 1;
    const reasonText = transitionReason.trim() || "Manual Grade Transition";

    return `This will scan every school and find active, mainstream Grade ${transitionGradeLevel} classes tagged "${transitionSourceYear}" that have at least one active student. Each eligible class gets a new Grade ${nextGrade} class tagged "${transitionNewYear}", and its students move across automatically. Classes with no active students, trial classes, and special-ed classes are skipped and left untouched. "${reasonText}" will be recorded against the transition for auditing.`;
  };

  const resetTransitionRunState = () => {
    setTransitionPreviewResult(null);
    setTransitionLiveResult(null);
    setTransitionAttempt(1);
    setTransitionConfirmChecked(false);
    setTransitionError(null);
  };

  const handleOpenGradeTransitionModal = () => {
    setTransitionGradeLevel(1);
    setTransitionSourceYear("");
    setTransitionNewYear("");
    setTransitionReason("");
    resetTransitionRunState();
    setShowGradeTransitionModal(true);
  };

  const handleCloseGradeTransitionModal = () => {
    setShowGradeTransitionModal(false);
  };

  const handleTransitionSelectionChange = (setter) => (value) => {
    setter(value);
    resetTransitionRunState();
  };

  const handlePreviewTransition = async () => {
    setTransitionError(null);
    setTransitionPreviewing(true);
    try {
      const key = buildTransitionIdempotencyKey({ dryRun: true, attempt: 1 });
      const result = await bulkTransitionGradeClasses(
        {
          currentGradeLevel: transitionGradeLevel,
          sourceAcademicYear: transitionSourceYear,
          newAcademicYear: transitionNewYear,
          transitionReason: transitionReason.trim() || undefined,
          dryRun: true,
        },
        key
      );
      setTransitionPreviewResult(result);
    } catch (err) {
      setTransitionError(err.response?.data?.message || err.message);
    } finally {
      setTransitionPreviewing(false);
    }
  };

  const handleRunTransition = async () => {
    setTransitionError(null);
    setTransitionRunning(true);
    try {
      const key = buildTransitionIdempotencyKey({ dryRun: false, attempt: transitionAttempt });
      const result = await bulkTransitionGradeClasses(
        {
          currentGradeLevel: transitionGradeLevel,
          sourceAcademicYear: transitionSourceYear,
          newAcademicYear: transitionNewYear,
          transitionReason: transitionReason.trim() || undefined,
          dryRun: false,
        },
        key
      );
      setTransitionLiveResult(result);
    } catch (err) {
      setTransitionError(err.response?.data?.message || err.message);
    } finally {
      setTransitionRunning(false);
    }
  };

  const handleRunTransitionAgain = () => {
    setTransitionAttempt((prev) => prev + 1);
    setTransitionLiveResult(null);
    setTransitionConfirmChecked(false);
    setTransitionError(null);
  };

  // Handle device orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await whoami();
        const stakeholder = response.data.stakeholder;
        setTeacherId(stakeholder.id);
        setName(stakeholder.name);
      } catch (error) {
        console.error("Error in whoami:", error);
      }
    };

    fetchUserInfo();
  }, []);

  // Fetch countries once on mount so both modals have them ready
  useEffect(() => {
    getCountries()
      .then(data => setCountries(data || []))
      .catch(err => console.error("Error fetching countries:", err));
  }, []);

  // Open modal and load data
  const handleOpenConfigModal = async () => {
    setShowConfigModal(true);
    setError(null);
    setSuccess(null);
    await loadConfigData();
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setError(null);
    setSuccess(null);
  };

  const loadConfigData = async () => {
    setLoadingConfig(true);
    setError(null);

    try {
      // Fetch config for each country
      const configs = {};
      await Promise.all(
        countries.map(async (country) => {
          try {
            const config = await getNextYearClassConfig(country.id);
            configs[country.id] = {
              enabled: config.enabled || false,
              availableYears: Array.isArray(config.availableYears) ? config.availableYears : []
            };
          } catch (err) {
            console.error(`Error fetching config for ${country.name}:`, err);
            configs[country.id] = { enabled: false, availableYears: [] };
          }
        })
      );

      setCountryConfigs(configs);
    } catch (err) {
      setError(`Failed to load configuration: ${err.message}`);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleToggleEnabled = (countryId) => {
    setCountryConfigs(prev => ({
      ...prev,
      [countryId]: {
        ...prev[countryId],
        enabled: !prev[countryId]?.enabled,
        availableYears: Array.isArray(prev[countryId]?.availableYears) ? prev[countryId].availableYears : []
      }
    }));
  };

  const handleToggleYear = (countryId, year) => {
    setCountryConfigs(prev => {
      const currentYears = Array.isArray(prev[countryId]?.availableYears) ? prev[countryId].availableYears : [];
      const newYears = currentYears.includes(year)
        ? currentYears.filter(y => y !== year)
        : [...currentYears, year];

      return {
        ...prev,
        [countryId]: {
          ...prev[countryId],
          availableYears: newYears
        }
      };
    });
  };

  const handleSaveCountry = async (countryId, countryName) => {
    if (!teacherId) {
      setError('User ID not available. Please refresh and try again.');
      return;
    }

    setSavingCountries(prev => new Set(prev).add(countryId));
    setError(null);
    setSuccess(null);

    try {
      const config = countryConfigs[countryId];
      const safeAvailableYears = Array.isArray(config.availableYears) ? config.availableYears : [];
      
      await updateNextYearClassConfig(
        countryId,
        config.enabled || false,
        safeAvailableYears
      );

      setSuccess(`Configuration saved for ${countryName}`);

      // Reload config for this country
      const updatedConfig = await getNextYearClassConfig(countryId);
      setCountryConfigs(prev => ({
        ...prev,
        [countryId]: {
          enabled: updatedConfig.enabled || false,
          availableYears: Array.isArray(updatedConfig.availableYears) ? updatedConfig.availableYears : []
        }
      }));
    } catch (err) {
      setError(`Failed to save configuration for ${countryName}: ${err.message}`);
    } finally {
      setSavingCountries(prev => {
        const next = new Set(prev);
        next.delete(countryId);
        return next;
      });
    }
  };

  // ─── Start Month handlers ─────────────────────────────────────────────────

  const handleOpenStartMonthModal = async () => {
    setShowStartMonthModal(true);
    setStartMonthError(null);
    setStartMonthSuccess(null);
    setLoadingStartMonth(true);

    try {
      const existingConfigs = await getAcademicYearConfigs();

      const configMap = {};
      (existingConfigs || []).forEach(c => {
        configMap[c.countryid] = { startMonth: c.startMonth, exists: true };
      });

      // Fill in defaults for countries with no config
      countries.forEach(country => {
        if (!configMap[country.id]) {
          configMap[country.id] = { startMonth: 8, exists: false };
        }
      });

      setStartMonthConfigs(configMap);
    } catch (err) {
      setStartMonthError(`Failed to load configurations: ${err.message}`);
    } finally {
      setLoadingStartMonth(false);
    }
  };

  const handleCloseStartMonthModal = () => {
    setShowStartMonthModal(false);
    setStartMonthError(null);
    setStartMonthSuccess(null);
  };

  const handleStartMonthChange = (countryId, month) => {
    setStartMonthConfigs(prev => ({
      ...prev,
      [countryId]: { ...prev[countryId], startMonth: parseInt(month, 10) }
    }));
  };

  const handleSaveStartMonth = async (countryId, countryName) => {
    setSavingStartMonth(prev => new Set(prev).add(countryId));
    setStartMonthError(null);
    setStartMonthSuccess(null);

    try {
      const { startMonth, exists } = startMonthConfigs[countryId];

      if (exists) {
        await updateAcademicYearConfig(countryId, startMonth);
      } else {
        await createAcademicYearConfig(countryId, startMonth);
      }

      setStartMonthConfigs(prev => ({
        ...prev,
        [countryId]: { startMonth, exists: true }
      }));
      setStartMonthSuccess(`Start month saved for ${countryName}`);
    } catch (err) {
      setStartMonthError(`Failed to save for ${countryName}: ${err.message}`);
    } finally {
      setSavingStartMonth(prev => {
        const next = new Set(prev);
        next.delete(countryId);
        return next;
      });
    }
  };

  const handleResetStartMonth = async (countryId, countryName) => {
    setSavingStartMonth(prev => new Set(prev).add(countryId));
    setStartMonthError(null);
    setStartMonthSuccess(null);

    try {
      await deleteAcademicYearConfig(countryId);
      setStartMonthConfigs(prev => ({
        ...prev,
        [countryId]: { startMonth: 8, exists: false }
      }));
      setStartMonthSuccess(`${countryName} reset to default (August)`);
    } catch (err) {
      setStartMonthError(`Failed to reset ${countryName}: ${err.message}`);
    } finally {
      setSavingStartMonth(prev => {
        const next = new Set(prev);
        next.delete(countryId);
        return next;
      });
    }
  };

  const handleApplyToAll = async () => {
    if (!teacherId) {
      setError('User ID not available. Please refresh and try again.');
      return;
    }

    if (countries.length === 0) {
      setError('No countries available to apply settings to.');
      return;
    }

    // Get the first country's config as the template
    const firstCountryId = countries[0].id;
    const templateConfig = countryConfigs[firstCountryId];

    if (!templateConfig) {
      setError('Please configure at least one country first.');
      return;
    }

    setLoadingConfig(true);
    setError(null);
    setSuccess(null);

    try {
      const safeAvailableYears = Array.isArray(templateConfig.availableYears) ? templateConfig.availableYears : [];
      
      await Promise.all(
        countries.map(country =>
          updateNextYearClassConfig(
            country.id,
            templateConfig.enabled || false,
            safeAvailableYears
          )
        )
      );

      setSuccess(`Configuration applied to all ${countries.length} countries`);
      await loadConfigData();
    } catch (err) {
      setError(`Failed to apply to all countries: ${err.message}`);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Admin function cards
  const globalFunctions = [
    {
      category: "Academic Year Configuration",
      description: "Manage the Create Class Academic Year dropdown across countries",
      items: [
        {
          name: "Next Year Class Configuration",
          key: "nextYearConfig",
          icon: Calendar,
          description: "Enable or disable the next academic year option in the Create Class dropdown by country",
          color: "#8b5cf6",
          action: () => handleOpenConfigModal()
        },
        {
          name: "Academic Year Start Month",
          key: "startMonthConfig",
          icon: Globe,
          description: "Set which month the academic year begins for each country. Defaults to August if not configured.",
          color: "#0ea5e9",
          action: () => handleOpenStartMonthModal()
        }
      ]
    },
    {
      category: "Grade Transitions",
      description: "Move classes up a grade level and into a new academic year, across every school",
      items: [
        {
          name: "Bulk Grade Transition",
          key: "bulkGradeTransition",
          icon: ArrowRepeat,
          description: "Transition all eligible classes at a grade level to the next grade, for every school in the system",
          color: "#16a34a",
          action: () => handleOpenGradeTransitionModal()
        }
      ]
    }
  ];

  const AdminFunctionCard = ({ item }) => (
    <Card
      className="class-admin-card"
      onClick={item.action}
      style={{ cursor: 'pointer' }}
    >
      <Card.Body className="class-admin-card-body">
        <div className="class-admin-icon" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
          <item.icon size={20} />
        </div>
        <div className="class-admin-content">
          <h6 className="class-admin-title">{item.name}</h6>
          <p className="class-admin-description">{item.description}</p>
        </div>
      </Card.Body>
    </Card>
  );

  const TransitionResultSummary = ({ result, isPreview }) => {
    if (!result) return null;
    const summary = result.summary || {};
    const schoolsToShow = (result.schoolBreakdown || []).filter(
      (school) => school.eligibleClasses > 0 || school.failedTransitions > 0
    );

    return (
      <div className="mt-3">
        <Alert variant={result.success ? "success" : "warning"} className="mb-3">
          {isPreview ? (
            <><EyeFill className="me-2" />Preview only — nothing was changed. This is what would happen if you run it for real.</>
          ) : result.success ? (
            <><CheckCircle className="me-2" />Run complete.</>
          ) : (
            <><ExclamationTriangleFill className="me-2" />Run completed with some failures — see details below.</>
          )}
        </Alert>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="secondary">{summary.eligibleClasses ?? 0} eligible classes</Badge>
          <Badge bg="success">{summary.successfulTransitions ?? 0} {isPreview ? "would succeed" : "transitioned"}</Badge>
          <Badge bg={summary.failedTransitions ? "danger" : "secondary"}>{summary.failedTransitions ?? 0} failed</Badge>
          <Badge bg="info">{summary.totalStudentsAffected ?? 0} students affected</Badge>
          <Badge bg="light" text="dark">{summary.schoolsWithClasses ?? 0} of {summary.totalSchools ?? 0} schools have classes</Badge>
        </div>

        {schoolsToShow.length > 0 && (
          <Table striped hover responsive size="sm" className="mb-3">
            <thead>
              <tr>
                <th>School</th>
                <th className="text-center">Eligible</th>
                <th className="text-center">{isPreview ? "Would succeed" : "Succeeded"}</th>
                <th className="text-center">Failed</th>
                <th className="text-center">Students</th>
              </tr>
            </thead>
            <tbody>
              {schoolsToShow.map((school) => (
                <tr key={school.schoolId}>
                  <td>{school.schoolName || school.schoolId}</td>
                  <td className="text-center">{school.eligibleClasses}</td>
                  <td className="text-center">{school.successfulTransitions}</td>
                  <td className="text-center">{school.failedTransitions}</td>
                  <td className="text-center">{school.studentsAffected}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {(result.errors || []).length > 0 && (
          <Alert variant="danger">
            <strong>Errors:</strong>
            <ul className="mb-0">
              {result.errors.map((err, idx) => (
                <li key={idx}>
                  {err.schoolId}: {(err.errors || []).join(", ")} ({err.classesAffected} class{err.classesAffected === 1 ? "" : "es"})
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    );
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} />

        <Row className="class-admin-row">
          <LeftPanel />

          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            {/* Page Header */}
            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Academic Year Control</h2>
              <p className="class-admin-page-subtitle">
                Manage the academic year options available in the Create Class dropdown across different countries
              </p>
            </div>

            {/* Error/Success messages */}
            {error && (
              <Alert variant="danger" className="mb-4">
                <strong>Error: </strong>{error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4">
                <strong>Success: </strong>{success}
              </Alert>
            )}

            {/* Global Functions - Staff only */}
            {!teacherMode && (
              <div className="admin-functions-container mb-4">
                {globalFunctions.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="function-category">
                    <h4 className="category-title">{category.category}</h4>
                    {category.description && (
                      <p className="category-description text-muted">{category.description}</p>
                    )}
                    <div className="function-cards-grid">
                      {category.items.map((item, itemIndex) => (
                        <AdminFunctionCard key={itemIndex} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Teacher mode message */}
            {teacherMode && (
              <Alert variant="info">
                <InfoCircleFill className="me-2" />
                This page is only available for staff users.
              </Alert>
            )}

          </Col>
        </Row>
      </Container>

      {/* Next Year Class Config Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showConfigModal}
          onHide={handleCloseConfigModal}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Calendar className="me-2" />
              Next Year Class Configuration
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {error && (
              <Alert variant="danger" className="mb-3">
                <ExclamationTriangleFill className="me-2" />
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-3">
                <CheckCircle className="me-2" />
                {success}
              </Alert>
            )}

            <Alert variant="info" className="mb-3">
              <InfoCircleFill className="me-2" />
              <strong>Configure by Country:</strong> Enable/disable the academic year dropdown and select which years are available for each country.
              Only paid customers in enabled countries will see this feature.
            </Alert>

            {loadingConfig ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="lg" />
                <p className="mt-3">Loading countries...</p>
              </div>
            ) : countries.length === 0 ? (
              <Alert variant="warning">
                <ExclamationTriangleFill className="me-2" />
                No countries found.
              </Alert>
            ) : (
              <>
                <div className="mb-3 d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={handleApplyToAll}
                    disabled={loadingConfig}
                  >
                    Apply to All Countries
                  </Button>
                </div>

                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>Country</th>
                      <th className="text-center" style={{ width: '15%' }}>Academic Year Control</th>
                      <th style={{ width: '45%' }}>Academic Year Selection</th>
                      <th className="text-center" style={{ width: '15%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map(country => {
                      const config = countryConfigs[country.id] || { enabled: false, availableYears: [] };
                      // Ensure availableYears is always an array
                      const safeConfig = {
                        enabled: config.enabled || false,
                        availableYears: Array.isArray(config.availableYears) ? config.availableYears : []
                      };
                      const isSaving = savingCountries.has(country.id);

                      return (
                        <tr key={country.id}>
                          <td>
                            <Globe className="me-2" size={16} />
                            <strong>{country.name}</strong>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              id={`enabled-${country.id}`}
                              checked={safeConfig.enabled}
                              onChange={() => handleToggleEnabled(country.id)}
                              disabled={isSaving}
                              label={safeConfig.enabled ? "ON" : "OFF"}
                              style={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                            />
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {AVAILABLE_YEARS.map(year => (
                                <Form.Check
                                  key={year}
                                  type="checkbox"
                                  id={`year-${country.id}-${year}`}
                                  label={year}
                                  checked={safeConfig.availableYears.includes(year)}
                                  onChange={() => handleToggleYear(country.id, year)}
                                  disabled={isSaving || !safeConfig.enabled}
                                  inline
                                />
                              ))}
                            </div>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSaveCountry(country.id, country.name)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    className="me-1"
                                  />
                                  Saving...
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={handleCloseConfigModal}
              >
                Close
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Academic Year Start Month Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showStartMonthModal}
          onHide={handleCloseStartMonthModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Globe className="me-2" />
              Academic Year Start Month
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {startMonthError && (
              <Alert variant="danger" className="mb-3">
                <ExclamationTriangleFill className="me-2" />
                {startMonthError}
              </Alert>
            )}

            {startMonthSuccess && (
              <Alert variant="success" className="mb-3">
                <CheckCircle className="me-2" />
                {startMonthSuccess}
              </Alert>
            )}

            <Alert variant="info" className="mb-3">
              <InfoCircleFill className="me-2" />
              <strong>Configure by Country:</strong> Set the month the academic year starts. Countries without a custom config default to <strong>August</strong>. Use "Reset to Default" to remove a custom setting.
            </Alert>

            {loadingStartMonth ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="lg" />
                <p className="mt-3">Loading countries...</p>
              </div>
            ) : countries.length === 0 ? (
              <Alert variant="warning">
                <ExclamationTriangleFill className="me-2" />
                No countries found.
              </Alert>
            ) : (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Country</th>
                    <th style={{ width: '25%' }}>Start Month</th>
                    <th className="text-center" style={{ width: '15%' }}>Status</th>
                    <th className="text-center" style={{ width: '25%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {countries.map(country => {
                    const cfg = startMonthConfigs[country.id] || { startMonth: 8, exists: false };
                    const isSaving = savingStartMonth.has(country.id);

                    return (
                      <tr key={country.id}>
                        <td>
                          <Globe className="me-2" size={16} />
                          <strong>{country.name}</strong>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={cfg.startMonth}
                            onChange={e => handleStartMonthChange(country.id, e.target.value)}
                            disabled={isSaving}
                          >
                            {MONTH_NAMES.map((month, idx) => (
                              <option key={idx + 1} value={idx + 1}>{month}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          {cfg.exists
                            ? <Badge bg="info">Custom</Badge>
                            : <Badge bg="secondary">Default</Badge>
                          }
                        </td>
                        <td className="text-center d-flex gap-1 justify-content-center">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleSaveStartMonth(country.id, country.name)}
                            disabled={isSaving}
                          >
                            {isSaving ? <Spinner as="span" animation="border" size="sm" /> : 'Save'}
                          </Button>
                          {cfg.exists && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleResetStartMonth(country.id, country.name)}
                              disabled={isSaving}
                            >
                              Reset
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={handleCloseStartMonthModal}>
                Close
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Bulk Grade Transition Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showGradeTransitionModal}
          onHide={handleCloseGradeTransitionModal}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <ArrowRepeat className="me-2" />
              Bulk Grade Transition
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="info" className="mb-3">
              <InfoCircleFill className="me-2" />
              Moves classes at a chosen grade level, tagged with a source academic year, up to
              the next grade in a new academic year. This runs across <strong>every school</strong>{" "}
              at once — it is not scoped to one school.
            </Alert>

            {transitionError && (
              <Alert variant="danger" className="mb-3">
                <ExclamationTriangleFill className="me-2" />
                {transitionError}
              </Alert>
            )}

            <Form>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current grade level</Form.Label>
                    <Form.Select
                      value={transitionGradeLevel}
                      onChange={(e) =>
                        handleTransitionSelectionChange(setTransitionGradeLevel)(parseInt(e.target.value, 10))
                      }
                    >
                      {TRANSITION_GRADE_LEVELS.map((level) => (
                        <option key={level} value={level}>Grade {level}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Classes currently at this grade will be evaluated for transition. They move
                      up to Grade {transitionGradeLevel + 1}.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Source academic year</Form.Label>
                    <Form.Select
                      value={transitionSourceYear}
                      onChange={(e) => handleTransitionSelectionChange(setTransitionSourceYear)(e.target.value)}
                    >
                      <option value="">Select a year…</option>
                      {AVAILABLE_YEARS.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Only classes currently tagged with this academic year are eligible.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>New academic year</Form.Label>
                    <Form.Select
                      value={transitionNewYear}
                      onChange={(e) => handleTransitionSelectionChange(setTransitionNewYear)(e.target.value)}
                    >
                      <option value="">Select a year…</option>
                      {AVAILABLE_YEARS.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      The new classes created by the transition will be tagged with this year.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Reason (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={transitionReason}
                  onChange={(e) => handleTransitionSelectionChange(setTransitionReason)(e.target.value)}
                  placeholder="Manual Grade Transition"
                  maxLength={200}
                />
                <Form.Text className="text-muted">
                  Recorded on the transition run for support/audit purposes. Defaults to
                  "Manual Grade Transition" if left blank.
                </Form.Text>
              </Form.Group>
            </Form>

            <Alert variant="secondary" className="mb-3">
              {getTransitionSummaryText()}
            </Alert>

            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-primary"
                onClick={handlePreviewTransition}
                disabled={!transitionSelectionValid || transitionPreviewing}
              >
                {transitionPreviewing ? (
                  <><Spinner as="span" animation="border" size="sm" className="me-2" />Previewing…</>
                ) : (
                  <><EyeFill className="me-2" />Preview changes (no data changed)</>
                )}
              </Button>
            </div>

            {transitionPreviewResult && (
              <TransitionResultSummary result={transitionPreviewResult} isPreview={true} />
            )}

            {transitionPreviewResult && transitionPreviewResult.success && (
              <>
                <hr />
                <h6>Run for real</h6>
                <p className="text-muted">
                  This will make permanent changes across every school shown above. Review the
                  preview carefully before continuing.
                </p>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="transition-confirm-checkbox"
                    checked={transitionConfirmChecked}
                    onChange={(e) => setTransitionConfirmChecked(e.target.checked)}
                    label="I've reviewed the preview and understand this will move students and cannot be easily undone."
                    disabled={!!transitionLiveResult}
                  />
                </Form.Group>

                {!transitionLiveResult && (
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="text-muted small">
                      Idempotency key for this run:{" "}
                      <code>{buildTransitionIdempotencyKey({ dryRun: false, attempt: transitionAttempt })}</code>
                      {transitionAttempt > 1 && " (fresh attempt — will not replay a prior cached result)"}
                    </div>
                    <Button
                      variant="success"
                      onClick={handleRunTransition}
                      disabled={!transitionConfirmChecked || transitionRunning}
                    >
                      {transitionRunning ? (
                        <><Spinner as="span" animation="border" size="sm" className="me-2" />Running…</>
                      ) : (
                        <><PlayFill className="me-2" />Run for real</>
                      )}
                    </Button>
                  </div>
                )}

                {transitionLiveResult && (
                  <>
                    <TransitionResultSummary result={transitionLiveResult} isPreview={false} />
                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <div className="text-muted small">
                        Running again reuses the same selections but uses a fresh key, so it will
                        genuinely re-run against current data instead of replaying this result.
                      </div>
                      <Button variant="outline-secondary" size="sm" onClick={handleRunTransitionAgain}>
                        <ArrowRepeat className="me-2" />Run again with a fresh key
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={handleCloseGradeTransitionModal}>
                Close
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default AcademicYearControl;